import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchSite, getToken, patchGallerySection, uploadFile } from './api';
import { AdminImagePreview, fileInput, input, label } from './adminUi';

function readAlbumsFromSite(site) {
  const fromConfig = Array.isArray(site?.gallery?.albums) ? site.gallery.albums : [];
  const fromItems = Array.isArray(site?.gallery?.items)
    ? site.gallery.items.map((row) => (row?.album || '').trim()).filter(Boolean)
    : [];
  return [...new Set([...fromConfig, ...fromItems])];
}

function readThumbnailsFromSite(site) {
  const raw = site?.gallery?.albumThumbnails;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const key = typeof k === 'string' ? k.trim() : '';
    const url = typeof v === 'string' ? v.trim() : '';
    if (key && url) out[key] = url;
  }
  return out;
}

export default function GalleryAlbums() {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [newAlbum, setNewAlbum] = useState('');
  const [newThumbnailSrc, setNewThumbnailSrc] = useState('');
  const [thumbUploading, setThumbUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    let cancelled = false;
    fetchSite()
      .then((site) => {
        if (cancelled) return;
        setAlbums(readAlbumsFromSite(site));
        setThumbnails(readThumbnailsFromSite(site));
      })
      .catch(() => {
        if (!cancelled) {
          setAlbums([]);
          setThumbnails({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const trimmedNewAlbum = useMemo(() => newAlbum.trim(), [newAlbum]);

  async function persist(nextAlbums, nextThumbnails) {
    setSaving(true);
    setErr('');
    setMsg('');
    try {
      const data = await patchGallerySection({
        albums: nextAlbums,
        albumThumbnails: nextThumbnails,
      });
      const g = data?.gallery;
      setAlbums(Array.isArray(g?.albums) ? g.albums : nextAlbums);
      setThumbnails(
        g?.albumThumbnails && typeof g.albumThumbnails === 'object' && !Array.isArray(g.albumThumbnails)
          ? readThumbnailsFromSite({ gallery: g })
          : nextThumbnails
      );
      setMsg('Album list saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function onNewThumbnailFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr('');
    setThumbUploading(true);
    try {
      const { url } = await uploadFile(file);
      setNewThumbnailSrc(url);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setThumbUploading(false);
      if (e.target) e.target.value = '';
    }
  }

  async function onRowThumbnailFile(name, e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr('');
    setThumbUploading(true);
    try {
      const { url } = await uploadFile(file);
      const nextThumbs = { ...thumbnails, [name]: url };
      await persist(albums, nextThumbs);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setThumbUploading(false);
      if (e.target) e.target.value = '';
    }
  }

  async function clearAlbumThumbnail(name) {
    if (!window.confirm(`Remove thumbnail for "${name}"?`)) return;
    const nextThumbs = { ...thumbnails };
    delete nextThumbs[name];
    await persist(albums, nextThumbs);
  }

  async function addAlbum(e) {
    e.preventDefault();
    if (!trimmedNewAlbum) return;
    if (albums.some((name) => name.toLowerCase() === trimmedNewAlbum.toLowerCase())) {
      setErr('Album already exists.');
      return;
    }
    const nextThumbs = { ...thumbnails };
    if (newThumbnailSrc) nextThumbs[trimmedNewAlbum] = newThumbnailSrc;
    await persist([...albums, trimmedNewAlbum], nextThumbs);
    setNewAlbum('');
    setNewThumbnailSrc('');
  }

  async function removeAlbum(name) {
    if (!window.confirm(`Remove album "${name}" from options?`)) return;
    const nextThumbs = { ...thumbnails };
    delete nextThumbs[name];
    await persist(
      albums.filter((row) => row !== name),
      nextThumbs
    );
  }

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <Link to="/admin/dashboard/gallery" className="text-xs font-medium text-brand-olive hover:underline">
            ← Back to gallery
          </Link>
          <h2 className="mt-1 text-lg font-bold text-neutral-900 md:text-xl">Create album</h2>
        </div>
      </header>

      <main className="min-h-0 w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {msg ? <p className="mb-4 text-sm text-green-700">{msg}</p> : null}
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}

        <section className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <form onSubmit={addAlbum} className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <label className={`${label} min-w-0 flex-1`}>
                Album name
                <input
                  className={input}
                  value={newAlbum}
                  onChange={(e) => setNewAlbum(e.target.value)}
                  placeholder="e.g. Farmer Meet 2026"
                  required
                />
              </label>
              <button
                type="submit"
                disabled={saving || thumbUploading || !trimmedNewAlbum}
                className="shrink-0 rounded-lg bg-brand-forest px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Add album'}
              </button>
            </div>

            <div className="border-t border-neutral-100 pt-4">
              <label className={label}>
                Album thumbnail (optional)
                <input
                  type="file"
                  accept="image/*"
                  className={fileInput}
                  disabled={saving || thumbUploading}
                  onChange={onNewThumbnailFile}
                />
              </label>
              {thumbUploading ? (
                <p className="mt-2 text-xs text-neutral-500">Uploading…</p>
              ) : null}
              <div className="mt-4 max-w-xs">
                <AdminImagePreview
                  src={newThumbnailSrc}
                  alt=""
                  emptyLabel="No thumbnail selected"
                />
              </div>
              {newThumbnailSrc ? (
                <button
                  type="button"
                  onClick={() => setNewThumbnailSrc('')}
                  className="mt-2 text-xs font-semibold text-neutral-600 underline hover:text-neutral-900"
                >
                  Clear thumbnail
                </button>
              ) : null}
            </div>
          </form>

          <div className="mt-8 border-t border-neutral-100 pt-6">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-600">Existing albums</h3>
            {albums.length === 0 ? (
              <p className="mt-3 text-sm text-neutral-500">No albums yet.</p>
            ) : (
              <ul className="mt-3 m-0 list-none space-y-3 p-0">
                {albums.map((name) => (
                  <li
                    key={name}
                    className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-neutral-200 bg-neutral-200/50">
                        {thumbnails[name] ? (
                          <img
                            src={thumbnails[name]}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-neutral-400">
                            No thumb
                          </div>
                        )}
                      </div>
                      <span className="min-w-0 text-sm font-medium text-neutral-800">{name}</span>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <label className="m-0 cursor-pointer">
                        <span className="inline-flex rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-800 hover:bg-neutral-100">
                          {thumbUploading ? '…' : 'Change image'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          disabled={saving || thumbUploading}
                          onChange={(e) => onRowThumbnailFile(name, e)}
                        />
                      </label>
                      {thumbnails[name] ? (
                        <button
                          type="button"
                          onClick={() => clearAlbumThumbnail(name)}
                          disabled={saving || thumbUploading}
                          className="rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 disabled:opacity-50"
                        >
                          Remove image
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => removeAlbum(name)}
                        disabled={saving || thumbUploading}
                        className="rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
